import { CloseCircleOutlined, DownloadOutlined, FilterOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { Badge, Button, DatePicker, Input, InputNumber, Select, Space, Table, Tooltip } from "antd";
import dayjs from "dayjs";
import type { ColumnType, TableProps } from "antd/es/table";
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiRequestParams, DataTableProps, PaginationConfig, SortConfig } from "./types";
import { debounce, exportToCSV, exportToExcel, getNestedValue } from "./utils";
import useDebounce from "@/hooks/useDebounce";
import { useUserStore } from "@/stores/userStore";

const { RangePicker } = DatePicker;
const { Option } = Select;

/**
 * DataTable Component
 *
 * A comprehensive, reusable data table component with:
 * - API data fetching
 * - Search functionality
 * - Filtering
 * - Sorting
 * - Pagination
 * - Export to CSV/Excel
 * - Custom action columns
 *
 * @example
 * ```tsx
 * <DataTable
 *   columns={columns}
 *   apiEndpoint="/api/users"
 *   searchable
 *   filters={filters}
 *   actionColumn={(record) => <Button>Edit</Button>}
 *   exportOptions={{ csv: true, excel: true }}
 * />
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DataTable = <T extends Record<string, any>>({
  columns,
  data: staticData,
  apiEndpoint,
  fetchData: customFetchData,
  searchable = false,
  searchPlaceholder = "Search...",
  defaultSearchValue = "",
  searchMatcher,
  filters = [],
  filterLayout = "wrap",
  collapsibleFilters = false,
  pagination = true,
  defaultPageSize = 10,
  rowKey = "id",
  actionColumn,
  actionColumnTitle = "Actions",
  actionColumnWidth = 150,
  exportOptions,
  loading: externalLoading,
  emptyText = "No data available",
  rowSelection,
  title,
  toolbarActions,
  tableProps = {},
  refreshKey,
  onChange,
  onDataChange,
  onRefresh,
  responsive = true,
  serverSide = false,
}: DataTableProps<T>) => {
  // State management
  const [data, setData] = useState<T[]>(staticData || []);
  const currentCompany = useUserStore((state) => state.currentCompany);
  const displayDateFormat = currentCompany?.dateFormat?.replace(/[/\-\.]/g, " ") || "DD MM YYYY";
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchInputValue, setSearchInputValue] = useState(defaultSearchValue);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  // Number range filter state (separate to handle min/max independently)
  const [numberRangeValues, setNumberRangeValues] = useState<Record<string, { min?: number; max?: number }>>({});
  // collapsibleFilters: whether the toolbar filter panel is open.
  const [filtersOpen, setFiltersOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const [sortConfig, setSortConfig] = useState<SortConfig | undefined>();
  const [paginationConfig, setPaginationConfig] = useState<PaginationConfig>({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    pageSizeOptions: ["10", "20", "50", "100"],
  });
  const lastEmittedDataRef = useRef<T[] | null>(null);

  // Debounce filter values to prevent excessive API calls
  const debouncedFilterValues = useDebounce(filterValues, 400);
  const debouncedNumberRangeValues = useDebounce(numberRangeValues, 400);
  const mergedDebouncedFilters = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mergedFilters: Record<string, any> = { ...debouncedFilterValues };
    Object.entries(debouncedNumberRangeValues).forEach(([key, range]) => {
      if (range.min !== undefined || range.max !== undefined) {
        mergedFilters[key] = [range.min, range.max];
      }
    });
    return mergedFilters;
  }, [debouncedFilterValues, debouncedNumberRangeValues]);

  useEffect(() => {
    if (!filters || filters.length === 0) return;
    const defaultFilters: Record<string, any> = {};
    filters.forEach((filter) => {
      if (filter.defaultValue !== undefined) {
        defaultFilters[filter.key] = filter.defaultValue;
      }
    });

    const needsInit = Object.keys(defaultFilters).some((key) => filterValues[key] === undefined);
    if (needsInit) {
      setFilterValues((prev) => ({ ...defaultFilters, ...prev }));
    }
  }, [filters, filterValues]);


  useEffect(() => {
    if (defaultSearchValue) {
      setSearchText(defaultSearchValue);
      setSearchInputValue(defaultSearchValue);
    }
  }, [defaultSearchValue]);

  /**
   * Fetch data from API
   */
  const fetchDataFromApi = useCallback(async () => {
    if (!apiEndpoint && !customFetchData) return;

    setLoading(true);
    try {
      const params: ApiRequestParams = {
        page: paginationConfig.current,
        pageSize: paginationConfig.pageSize,
        search: searchText || undefined,
        sort: sortConfig,
        filters: Object.keys(mergedDebouncedFilters).length > 0 ? mergedDebouncedFilters : undefined,
      };

      let response;
      if (customFetchData) {
        response = await customFetchData(params);
      } else if (apiEndpoint) {
        const { data: apiData } = await axios.get(apiEndpoint, { params });
        response = apiData;
      }

      if (response) {
        setData(response.data || []);
        if (response.pagination) {
          setPaginationConfig((prev) => ({
            ...prev,
            total: response.pagination?.total || 0,
            current: response.pagination?.page || prev.current,
            pageSize: response.pagination?.pageSize || prev.pageSize,
          }));
        }
      }

      // Notify parent of changes
      if (onChange) {
        onChange(params);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    apiEndpoint,
    customFetchData,
    paginationConfig.current,
    paginationConfig.pageSize,
    searchText,
    sortConfig,
    mergedDebouncedFilters,
    onChange,
  ]);

  /**
   * Effect to fetch data when dependencies change
   */
  useEffect(() => {
    if (staticData) {
      setData(staticData);
      setPaginationConfig((prev) => ({
        ...prev,
        total: staticData.length,
        current: prev.current,
      }));
    } else {
      fetchDataFromApi();
    }
    // re-run when parent provides a new refreshKey
  }, [staticData, fetchDataFromApi, refreshKey]);

  useEffect(() => {
    // In static data mode, allow parent to react to filter/search/sort changes (e.g. trigger API refetch).
    if (apiEndpoint || customFetchData || !onChange) return;

    const params: ApiRequestParams = {
      search: searchText || undefined,
      sort: sortConfig,
      filters: Object.keys(mergedDebouncedFilters).length > 0 ? mergedDebouncedFilters : undefined,
    };

    onChange(params);
  }, [apiEndpoint, customFetchData, onChange, refreshKey, searchText, sortConfig, mergedDebouncedFilters]);

  /**
   * Debounced search handler
   */
  const debouncedSearch = useMemo(
    () =>
      debounce((value: unknown) => {
        setSearchText(value as string);
        setPaginationConfig((prev) => ({ ...prev, current: 1 }));
      }, 500),
    [],
  );

  /**
   * Handle search input change
   */
  const handleSearch = (value: string) => {
    setSearchInputValue(value);
    debouncedSearch(value);
  };

  /**
   * Handle filter change
   */
  const handleFilterChange = (key: string, value: string | number | boolean | undefined) => {
    setFilterValues((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPaginationConfig((prev) => ({ ...prev, current: 1 }));
  };

  /**
   * Reset every toolbar filter to its default (unset unless the filter defines a defaultValue).
   */
  const clearAllFilters = () => {
    const defaults: typeof filterValues = {};
    filters.forEach((filter) => {
      defaults[filter.key] = filter.defaultValue;
    });
    setFilterValues(defaults);
    setNumberRangeValues({});
    setPaginationConfig((prev) => ({ ...prev, current: 1 }));
  };

  /**
   * Handle number range filter change
   */
  const handleNumberRangeChange = (key: string, type: "min" | "max", value: number | null) => {
    setNumberRangeValues((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [type]: value ?? undefined,
      },
    }));
    setPaginationConfig((prev) => ({ ...prev, current: 1 }));
  };

  /**
   * Handle column filter change
   */
  const handleColumnFilterChange = useCallback((columnKey: string, filterType: string, value: unknown) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnKey]: {
        ...prev[columnKey],
        [filterType]: value,
      },
    }));
    setPaginationConfig((prev) => ({ ...prev, current: 1 }));
  }, []);

  /**
   * Clear column filter
   */
  const clearColumnFilter = useCallback((columnKey: string) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[columnKey];
      return newFilters;
    });
    setPaginationConfig((prev) => ({ ...prev, current: 1 }));
  }, []);

  /**
   * Apply column filters and close popover
   */
  const applyColumnFilter = useCallback(() => {
    // Filter dropdown will close automatically
    // Force a re-render to apply filters
    setPaginationConfig((prev) => ({ ...prev, current: 1 }));
  }, []);

  /**
   * Handle table change (pagination, sorting)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleTableChange: TableProps<T>["onChange"] = (pagination, _filters, sorter: any) => {
    // Update pagination
    if (pagination) {
      setPaginationConfig((prev) => ({
        ...prev,
        current: pagination.current || 1,
        pageSize: pagination.pageSize || prev.pageSize,
      }));

      // Remote-paginated tables (apiEndpoint/customFetchData/serverSide) rely on the parent
      // to refetch the new page — internal paginationConfig alone gets overwritten by the
      // parent's own `pagination` prop on the next render otherwise.
      if ((apiEndpoint || customFetchData || serverSide) && onChange) {
        onChange({ page: pagination.current, pageSize: pagination.pageSize });
      }
    }

    // Update sorting
    if (sorter && sorter.field) {
      setSortConfig({
        field: Array.isArray(sorter.field) ? sorter.field.join(".") : sorter.field,
        order: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      setSortConfig(undefined);
    }
  };

  /**
   * Get unique values from column for filter options
   */
  const getUniqueColumnValues = useCallback(
    (columnKey: string, dataIndex?: string | string[]) => {
      const values = new Set<string>();
      data.forEach((item) => {
        const value = getNestedValue(item, dataIndex || columnKey);
        if (value !== null && value !== undefined && value !== "") {
          values.add(String(value));
        }
      });
      return Array.from(values).sort();
    },
    [data],
  );

  /**
   * Handle export
   */
  const handleExport = (type: "csv" | "excel") => {
    const filename = exportOptions?.filename || "export";
    const exportData = filteredData as Record<string, unknown>[];
    const exportColumns = columns as DataTableProps<Record<string, unknown>>["columns"];

    if (exportOptions?.onExport) {
      exportOptions.onExport(type, exportData);
      return;
    }

    if (type === "csv") {
      exportToCSV(exportData, exportColumns, filename);
    } else {
      exportToExcel(exportData, exportColumns, filename);
    }
  };

  /**
   * Render column filter dropdown content
   */
  const renderColumnFilterDropdown = useCallback(
    (column: (typeof columns)[0]) => {
      const columnKey = column.key;
      const currentFilter = columnFilters[columnKey] || {};
      const uniqueValues = getUniqueColumnValues(columnKey, column.dataIndex);

      return (
        <div className="bg-white rounded-lg shadow-lg p-4" style={{ width: "320px" }}>
          {/* Header */}
          <div className="flex items-center gap-2 mb-4 pb-3 border-b">
            <FilterOutlined className="text-blue-500" />
            <span className="text-sm font-semibold text-gray-900">Filter {column.title}</span>
          </div>

          <div className="space-y-3">
            {/* Text Search */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Search {column.title}</label>
              <Input
                placeholder={`Search by ${String(column.title).toLowerCase()}...`}
                value={currentFilter.search || ""}
                onChange={(e) => handleColumnFilterChange(columnKey, "search", e.target.value)}
                allowClear
                prefix={<SearchOutlined className="text-gray-400" />}
              />
            </div>

            {/* Select from unique values */}
            {uniqueValues.length > 0 && uniqueValues.length <= 50 && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Select Value</label>
                <Select
                  placeholder="Select value"
                  style={{ width: "100%" }}
                  value={currentFilter.exact}
                  onChange={(value) => handleColumnFilterChange(columnKey, "exact", value)}
                  allowClear
                  showSearch
                  suffixIcon={<span className="text-gray-400">▼</span>}
                >
                  {uniqueValues.map((value) => (
                    <Select.Option key={value} value={value}>
                      {value}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            {/* Date Range for date columns */}
            {(columnKey.toLowerCase().includes("date") ||
              columnKey.toLowerCase().includes("created") ||
              columnKey.toLowerCase().includes("updated") ||
              columnKey.toLowerCase().includes("submitted") ||
              columnKey.toLowerCase().includes("due")) && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Date Range</label>
                  <RangePicker
                    style={{ width: "100%" }}
                    value={currentFilter.dateRange}
                    onChange={(dates: any) => handleColumnFilterChange(columnKey, "dateRange", dates)}
                    placeholder={["From Date", "To Date"]}
                  />
                </div>
              )}

            {/* Number Range for amount/number columns */}
            {(columnKey.toLowerCase().includes("amount") ||
              columnKey.toLowerCase().includes("price") ||
              columnKey.toLowerCase().includes("total") ||
              columnKey.toLowerCase().includes("commission")) && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Amount Range</label>
                  <div className="flex items-center gap-2">
                    <InputNumber
                      placeholder="Min"
                      style={{ width: "100%" }}
                      value={currentFilter.min}
                      onChange={(value) => handleColumnFilterChange(columnKey, "min", value)}
                      prefix="$"
                    />
                    <span className="text-gray-400">-</span>
                    <InputNumber
                      placeholder="Max"
                      style={{ width: "100%" }}
                      value={currentFilter.max}
                      onChange={(value) => handleColumnFilterChange(columnKey, "max", value)}
                      prefix="$"
                    />
                  </div>
                </div>
              )}
          </div>

          {/* Action Buttons */}
          {/* <div className="flex justify-between gap-2 pt-3 mt-4 border-t">
            <Button size="small" onClick={() => clearColumnFilter(columnKey)} className="text-gray-600">
              Clear All Filters 1
            </Button>
            <Button type="primary" size="small" onClick={applyColumnFilter}>
              Apply
            </Button>
          </div> */}
        </div>
      );
    },
    [columnFilters, getUniqueColumnValues, handleColumnFilterChange, clearColumnFilter, applyColumnFilter],
  );

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    } else {
      fetchDataFromApi();
    }
  };

  /**
   * Convert DataTable columns to Ant Design columns
   */
  const antColumns: ColumnType<T>[] = useMemo(() => {
    const cols: ColumnType<T>[] = columns.map((col) => ({
      key: col.key,
      title: col.title,
      dataIndex: col.dataIndex || col.key,
      width: col.width,
      fixed: col.fixed,
      align: col.align,
      sorter: col.sortable ? true : undefined,
      // Only add custom filter dropdown if explicitly enabled with filterable: true
      ...(col.filterable === true && {
        filterDropdown: () => renderColumnFilterDropdown(col),
        filterIcon: (filtered: boolean) => (
          <FilterOutlined
            style={{
              color: columnFilters[col.key] || filtered ? "#3b82f6" : undefined,
            }}
          />
        ),
        filtered: !!columnFilters[col.key],
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filters: col.filters as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onFilter: col.onFilter as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (value: any, record: T, index: number) => {
        if (col.render) {
          return col.render(value, record, index);
        }

        // Handle nested values
        if (col.dataIndex) {
          const nestedValue = getNestedValue(record as Record<string, unknown>, col.dataIndex);
          return nestedValue as React.ReactNode;
        }

        return value;
      },
      responsive: responsive && col.hideOnMobile ? ["md"] : undefined,
    }));

    // Add action column if provided
    if (actionColumn) {
      cols.push({
        key: "actions",
        title: actionColumnTitle,
        width: actionColumnWidth,
        fixed: "right",
        render: (_: unknown, record: T, index: number) => actionColumn(record, index),
      });
    }

    return cols;
  }, [
    columns,
    actionColumn,
    actionColumnTitle,
    actionColumnWidth,
    responsive,
    renderColumnFilterDropdown,
    columnFilters,
  ]);

  /**
   * Filter data locally if using static data
   */
  const filteredData = useMemo(() => {
    if (apiEndpoint || customFetchData || serverSide) {
      return data; // API or Server handles filtering
    }

    let result = [...data];

    // Apply search
    if (searchText) {
      result = result.filter((item) => {
        if (searchMatcher) {
          return searchMatcher(item, searchText, columns);
        }

        return columns.some((col) => {
          const value = getNestedValue(item, col.dataIndex || col.key);
          return String(value).toLowerCase().includes(searchText.toLowerCase());
        });
      });
    }

    const getFilterComparableValues = (rawValue: unknown): string[] => {
      if (rawValue === undefined || rawValue === null || rawValue === "") return [];

      if (Array.isArray(rawValue)) {
        return rawValue.flatMap((item) => getFilterComparableValues(item));
      }

      if (typeof rawValue === "object") {
        const objectValue = rawValue as Record<string, unknown>;
        const preferredKeys = ["companyName", "vendorName", "name", "label", "value", "id"];
        const firstPreferredKey = preferredKeys.find((key) => {
          const candidate = objectValue[key];
          return candidate !== undefined && candidate !== null && candidate !== "";
        });

        if (firstPreferredKey) {
          return [String(objectValue[firstPreferredKey])];
        }

        return [];
      }

      return [String(rawValue)];
    };

    // Apply toolbar filters
    Object.entries(filterValues).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;

      // Find the filter config to get the type
      const filterConfig = filters.find((f) => f.key === key);
      if (filterConfig?.serverOnly) return;

      result = result.filter((item) => {
        const itemValue = getNestedValue(item, filterConfig?.dataIndex || key);

        // Handle date range filters (value is an array of dayjs objects)
        if (filterConfig?.type === "dateRange" && Array.isArray(value) && value.length === 2) {
          if (!itemValue) return false;
          const itemDate = new Date(itemValue as string);
          if (isNaN(itemDate.getTime())) return false;

          const startDate = value[0]?.toDate?.() || (value[0] ? new Date(value[0]) : null);
          const endDate = value[1]?.toDate?.() || (value[1] ? new Date(value[1]) : null);

          if (startDate) {
            startDate.setHours(0, 0, 0, 0);
            if (itemDate < startDate) return false;
          }
          if (endDate) {
            endDate.setHours(23, 59, 59, 999);
            if (itemDate > endDate) return false;
          }
          return true;
        }

        // Handle multiSelect (value is an array but NOT a date range)
        if (Array.isArray(value) && filterConfig?.type === "multiSelect") {
          const selectedValues = value.map((v) => String(v));
          const itemValues = getFilterComparableValues(itemValue);
          return itemValues.some((item) => selectedValues.includes(item));
        }

        // Handle boolean comparison explicitly
        if (typeof value === "boolean") {
          return itemValue === value;
        }

        // Handle string/number comparison
        const itemValues = getFilterComparableValues(itemValue);
        return itemValues.some((item) => item === String(value));
      });
    });

    // Apply toolbar number range filters (stored separately from filterValues)
    Object.entries(numberRangeValues).forEach(([key, range]) => {
      if (range.min === undefined && range.max === undefined) return;

      const filterConfig = filters.find((f) => f.key === key);
      result = result.filter((item) => {
        const itemValue = getNestedValue(item, filterConfig?.dataIndex || key);
        const numValue =
          typeof itemValue === "number" ? itemValue : parseFloat(String(itemValue).replace(/[^0-9.-]/g, ""));
        if (isNaN(numValue)) return false;
        if (range.min !== undefined && numValue < range.min) return false;
        if (range.max !== undefined && numValue > range.max) return false;
        return true;
      });
    });

    // Apply column filters
    Object.entries(columnFilters).forEach(([columnKey, filters]) => {
      const filterObj = filters as Record<string, unknown>;

      result = result.filter((item) => {
        const column = columns.find((col) => col.key === columnKey);
        const itemValue = getNestedValue(item, column?.dataIndex || columnKey);
        const itemValueStr = String(itemValue).toLowerCase();

        // Apply search filter
        if (
          filterObj.search &&
          typeof filterObj.search === "string" &&
          !itemValueStr.includes(filterObj.search.toLowerCase())
        ) {
          return false;
        }

        // Apply exact match filter
        if (filterObj.exact && String(itemValue) !== String(filterObj.exact)) {
          return false;
        }

        // Apply number range filter
        if (filterObj.min !== undefined && filterObj.min !== null) {
          const minValue = typeof filterObj.min === "number" ? filterObj.min : parseFloat(String(filterObj.min));
          const numValue =
            typeof itemValue === "number" ? itemValue : parseFloat(String(itemValue).replace(/[^0-9.-]/g, ""));
          if (isNaN(numValue) || numValue < minValue) {
            return false;
          }
        }
        if (filterObj.max !== undefined && filterObj.max !== null) {
          const maxValue = typeof filterObj.max === "number" ? filterObj.max : parseFloat(String(filterObj.max));
          const numValue =
            typeof itemValue === "number" ? itemValue : parseFloat(String(itemValue).replace(/[^0-9.-]/g, ""));
          if (isNaN(numValue) || numValue > maxValue) {
            return false;
          }
        }

        // Apply date range filter
        if (filterObj.dateRange && Array.isArray(filterObj.dateRange) && filterObj.dateRange.length === 2) {
          const itemDate = new Date(itemValue as string);
          const startDate = filterObj.dateRange[0]?.toDate();
          const endDate = filterObj.dateRange[1]?.toDate();
          if (startDate && itemDate < startDate) return false;
          if (endDate && itemDate > endDate) return false;
        }

        return true;
      });
    });

    // Apply sorting for static data
    if (sortConfig) {
      result.sort((a, b) => {
        const aValue = getNestedValue(a, sortConfig.field);
        const bValue = getNestedValue(b, sortConfig.field);

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        // Handle different data types
        let comparison = 0;

        // Try numeric comparison first
        const aNum = Number(aValue);
        const bNum = Number(bValue);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          comparison = aNum - bNum;
        } else if (aValue instanceof Date && bValue instanceof Date) {
          // Date comparison
          comparison = aValue.getTime() - bValue.getTime();
        } else {
          // String comparison
          comparison = String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: "base" });
        }

        return sortConfig.order === "asc" ? comparison : -comparison;
      });
    }

    return result;
  }, [
    data,
    searchText,
    filterValues,
    numberRangeValues,
    columnFilters,
    columns,
    filters,
    apiEndpoint,
    customFetchData,
    serverSide,
    sortConfig,
    searchMatcher,
  ]);

  useEffect(() => {
    if (!onDataChange) return;

    const prev = lastEmittedDataRef.current;
    const isSameAsPrevious =
      !!prev && prev.length === filteredData.length && prev.every((row, index) => row === filteredData[index]);

    if (isSameAsPrevious) return;

    lastEmittedDataRef.current = filteredData;
    onDataChange(filteredData);
  }, [filteredData, onDataChange]);

  /**
   * Render filter inputs
   */
  const isGridLayout = filterLayout === "grid";
  // Grid layout: every control fills its grid cell. Wrap layout: the filter's own width, else a fallback.
  const controlStyle = (width: number | string | undefined, fallback?: React.CSSProperties): React.CSSProperties | undefined =>
    isGridLayout ? { width: "100%" } : width !== undefined ? { width } : fallback;

  const renderFilters = () => {
    if (filters.length === 0) return null;

    const controls = filters.map((filter) => {
          switch (filter.type) {
            case "select":
            case "multiSelect":
              return (
                <Select
                  key={filter.key}
                  placeholder={filter.placeholder || filter.label}
                  style={controlStyle(filter.width, { minWidth: 150 })}
                  // With a fixed width, let the dropdown grow so long options aren't cut off.
                  popupMatchSelectWidth={filter.width === undefined && !isGridLayout}
                  onChange={(value) => handleFilterChange(filter.key, value as string | number | boolean | undefined)}
                  allowClear
                  showSearch
                  // Search by the visible label, not the option value (often an ID).
                  optionFilterProp="children"
                  maxTagCount={filter.type === "multiSelect" ? "responsive" : undefined}
                  mode={filter.type === "multiSelect" ? "multiple" : undefined}
                  // Controlled by filterValues (like the date ranges) so "Clear all" can reset it.
                  value={(filter.key in filterValues ? filterValues[filter.key] : filter.defaultValue) as string | number | undefined}
                >
                  {filter.options?.map((option) => (
                    <Option key={String(option.value)} value={option.value as string | number}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              );
            case "dateRange":
              return (
                <RangePicker
                  key={filter.key}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  value={filter.key in filterValues ? filterValues[filter.key] : filter.defaultValue}
                  onChange={(dates: any) => handleFilterChange(filter.key, dates as any)}
                  placeholder={filter.rangePlaceholder ?? [filter.placeholder || "Start Date", "End Date"]}
                  format={displayDateFormat}
                  style={controlStyle(filter.width)}
                />
              );
            case "search":
              return (
                <Input
                  key={filter.key}
                  placeholder={filter.placeholder || filter.label}
                  prefix={<SearchOutlined />}
                  onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                  style={controlStyle(filter.width, { minWidth: 200 })}
                  allowClear
                  value={((filter.key in filterValues ? filterValues[filter.key] : filter.defaultValue) as string | undefined) ?? ""}
                />
              );
            case "numberRange":
              return (
                <div key={filter.key} className="flex items-center gap-1">
                  <span className="text-sm text-gray-600 mr-1">{filter.label}:</span>
                  <InputNumber
                    placeholder={filter.minPlaceholder || "Min"}
                    style={{ width: 100 }}
                    value={numberRangeValues[filter.key]?.min}
                    onChange={(value) => handleNumberRangeChange(filter.key, "min", value)}
                    prefix={filter.prefix}
                  />
                  <span className="text-gray-400">-</span>
                  <InputNumber
                    placeholder={filter.maxPlaceholder || "Max"}
                    style={{ width: 100 }}
                    value={numberRangeValues[filter.key]?.max}
                    onChange={(value) => handleNumberRangeChange(filter.key, "max", value)}
                    prefix={filter.prefix}
                  />
                </div>
              );
            default:
              return null;
          }
        });

    // Grid layout (and the collapsible panel) render their own container.
    return isGridLayout || collapsibleFilters ? controls : <div className="flex flex-wrap gap-3">{controls}</div>;
  };

  // Number of toolbar filters currently set — shown on the collapsible "Filters" button.
  const hasValue = (value: unknown): boolean =>
    Array.isArray(value) ? value.some((v) => v !== undefined && v !== null && v !== "") : value !== undefined && value !== null && value !== "";
  const activeFilterCount = filters.filter((filter) =>
    filter.type === "numberRange"
      ? hasValue(numberRangeValues[filter.key]?.min) || hasValue(numberRangeValues[filter.key]?.max)
      : hasValue(filterValues[filter.key]),
  ).length;

  // Each active toolbar filter as "Label: value(s)" — shown on hover over the "Filters" button.
  const activeFilterSummary = filters.flatMap((filter) => {
    const value = filterValues[filter.key];
    const optionLabel = (v: unknown) => String(filter.options?.find((o) => o.value === v)?.label ?? v);
    const formatDate = (d: unknown) => (dayjs.isDayjs(d) && d.isValid() ? d.format(displayDateFormat) : undefined);
    let text: string | undefined;
    switch (filter.type) {
      case "select":
      case "multiSelect": {
        const values = (Array.isArray(value) ? value : [value]).filter((v) => hasValue(v));
        text = values.length ? values.map(optionLabel).join(", ") : undefined;
        break;
      }
      case "dateRange": {
        const [from, to] = Array.isArray(value) ? value.map(formatDate) : [];
        text = from || to ? `${from ?? "…"} → ${to ?? "…"}` : undefined;
        break;
      }
      case "numberRange": {
        const range = numberRangeValues[filter.key];
        text = hasValue(range?.min) || hasValue(range?.max) ? `${range?.min ?? "…"} – ${range?.max ?? "…"}` : undefined;
        break;
      }
      default:
        text = hasValue(value) ? String(value) : undefined;
    }
    return text ? [{ key: filter.key, label: filter.label, text }] : [];
  });

  const filterGridClassName = "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4";

  /**
   * Render toolbar
   */
  const renderToolbar = () => {
    const hasToolbarContent = searchable || filters.length > 0 || exportOptions || toolbarActions || onRefresh;
    if (!hasToolbarContent) return null;

    const searchInput = searchable && (
      <Input
        placeholder={searchPlaceholder}
        prefix={<SearchOutlined />}
        value={searchInputValue}
        onChange={(e) => handleSearch(e.target.value)}
        style={isGridLayout && !collapsibleFilters ? undefined : { maxWidth: 300 }}
        allowClear
      />
    );

    const actions = (
      <Space>
        {toolbarActions}

        {onRefresh && (
          <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
            Refresh
          </Button>
        )}

        {exportOptions?.csv && (
          <Button icon={<DownloadOutlined />} onClick={() => handleExport("csv")}>
            Export CSV
          </Button>
        )}

        {exportOptions?.excel && (
          <Button icon={<DownloadOutlined />} onClick={() => handleExport("excel")}>
            Export Excel
          </Button>
        )}
      </Space>
    );

    if (collapsibleFilters && filters.length > 0) {
      return (
        <div className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
              {searchInput}
              <Tooltip
                placement="bottomLeft"
                overlayStyle={{ maxWidth: 360 }}
                title={
                  activeFilterSummary.length > 0 ? (
                    <div className="space-y-1 py-0.5">
                      <div className="text-[11px] font-semibold uppercase tracking-wide opacity-70">Active filters</div>
                      {activeFilterSummary.map((item) => (
                        <div key={item.key} className="text-xs">
                          <span className="font-semibold">{item.label}:</span> {item.text}
                        </div>
                      ))}
                    </div>
                  ) : (
                    "No filters applied"
                  )
                }
              >
                <Button
                  icon={<FilterOutlined />}
                  type={filtersOpen ? "primary" : "default"}
                  ghost={filtersOpen}
                  aria-expanded={filtersOpen}
                  onClick={() => setFiltersOpen((open) => !open)}
                >
                  Filters
                  {activeFilterCount > 0 && <Badge count={activeFilterCount} size="small" color="#2563eb" className="ml-1" />}
                </Button>
              </Tooltip>
              {/* Always shown next to Filters; disabled while no filter is set. */}
              <Button icon={<CloseCircleOutlined />} onClick={clearAllFilters} disabled={activeFilterCount === 0}>
                Clear all filters
              </Button>
            </div>
            {actions}
          </div>
          {/* Kept mounted while closed: the selects hold their own values and must survive a toggle. */}
          <div className={filtersOpen ? "mt-3 rounded-lg border border-gray-100 bg-gray-50 p-3" : "hidden"}>
            <div className={isGridLayout ? filterGridClassName : "flex flex-wrap gap-3"}>{renderFilters()}</div>
          </div>
        </div>
      );
    }

    return (
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className={isGridLayout ? `${filterGridClassName} min-w-0 flex-1` : "flex flex-wrap items-center gap-3 flex-1"}>
          {searchInput}
          {renderFilters()}
        </div>

        {actions}
      </div>
    );
  };

  const isRemoteMode = Boolean(apiEndpoint || customFetchData || serverSide);
  const tablePagination = useMemo(() => {
    if (!pagination) return false;

    const basePagination = typeof pagination === "boolean" ? paginationConfig : { ...paginationConfig, ...pagination };

    // In static-data mode, pagination should reflect rows currently visible in the table.
    if (!isRemoteMode) {
      return { ...basePagination, total: filteredData.length };
    }

    return basePagination;
  }, [pagination, paginationConfig, isRemoteMode, filteredData.length]);

  return (
    <div>
      {title && <div className="mb-4">{title}</div>}

      {renderToolbar()}

      <Table<T>
        {...tableProps}
        columns={antColumns}
        dataSource={filteredData}
        loading={externalLoading !== undefined ? externalLoading : loading}
        pagination={tablePagination}
        onChange={handleTableChange}
        rowKey={rowKey}
        rowSelection={rowSelection}
        locale={{ emptyText }}
        scroll={{ x: "max-content" }}
        className="bg-white rounded-lg shadow-sm"
      />
    </div>
  );
};

export default DataTable;
